// src/pages/Builder.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../utils/supabaseClient';
import { industrySections } from '../data/industrySections';
import ResumeForm from '../components/Resume/ResumeForm';
import ResumePreview from '../components/Resume/ResumePreview';
import ResumeDownload from '../components/Resume/ResumeDownload';
import TXTDownload from '../components/Resume/TXTDownload';
import { Save, ArrowLeft, Eye, Sparkles, FileText } from 'lucide-react';
import toast from 'react-hot-toast';

const Builder = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('resume');
  const [isNewResume, setIsNewResume] = useState(true);

  // Get industry from URL
  const [selectedIndustry, setSelectedIndustry] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('industry') || 'softwareEngineer';
  });

  // Get industry data
  const industryData = industrySections[selectedIndustry] || null;
  const sections = industryData?.sections || [];

  // ===== SINGLE FORM DATA STATE =====
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    address: '',
    photo_url: '',
    title: '',
    summary: '',
    education: [],
    experience: [],
    skills: [],
    certifications: [],
    languages: []
  });

  const [coverLetterData, setCoverLetterData] = useState({
    recipient_name: '',
    company_name: '',
    position: '',
    body: '',
    closing: 'Sincerely',
  });

  // ===== INITIALIZE INDUSTRY DATA =====
  useEffect(() => {
    if (sections && sections.length > 0 && !id) {
      setFormData(prev => {
        const newData = { ...prev };
        sections.forEach(section => {
          if (newData[section.id] === undefined) {
            let defaultValue;
            switch (section.type) {
              case 'flyingHours':
                defaultValue = {};
                if (section.fields) {
                  section.fields.forEach(field => {
                    defaultValue[field] = '';
                  });
                }
                break;
              case 'skills':
                defaultValue = section.defaultSkills || [];
                break;
              case 'text':
              case 'textarea':
                defaultValue = '';
                break;
              case 'experience':
              case 'flight-experience':
              case 'projects':
              case 'publications':
              case 'education':
              case 'certifications':
                defaultValue = [];
                break;
              default:
                defaultValue = '';
            }
            newData[section.id] = defaultValue;
          }
        });
        return newData;
      });
    }
  }, [sections, id]);

  // ===== FETCH EXISTING RESUME =====
  useEffect(() => {
    if (id) {
      setIsNewResume(false);
      fetchResume();
    } else {
      setIsNewResume(true);
      setLoading(false);
    }
  }, [id]);

  const fetchResume = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('resumes')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) {
        toast.error('Failed to load resume');
        navigate('/dashboard');
        return;
      }
      
      if (data) {
        setFormData({
          full_name: data.full_name || '',
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || '',
          photo_url: data.photo_url || '',
          title: data.title || '',
          summary: data.summary || '',
          education: data.education || [],
          experience: data.experience || [],
          skills: data.skills || [],
          certifications: data.certifications || [],
          languages: data.languages || [],
          ...(data.industry_data || {})
        });

        setCoverLetterData({
          recipient_name: data.cover_letter_recipient || '',
          company_name: data.cover_letter_company || '',
          position: data.cover_letter_position || '',
          body: data.cover_letter_body || '',
          closing: data.cover_letter_closing || 'Sincerely',
        });
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load resume');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  // ===== SAVE RESUME =====
  const saveResume = async () => {
    if (!user) return;
    
    if (!formData.full_name) {
      toast.error('Please enter your full name');
      return;
    }
    if (!formData.email) {
      toast.error('Please enter your email');
      return;
    }

    setSaving(true);
    try {
      const personalFields = ['full_name', 'email', 'phone', 'address', 'photo_url', 'title', 'summary', 'education', 'experience', 'skills', 'certifications', 'languages'];
      const personalData = {};
      const industryDataObj = {};
      
      Object.keys(formData).forEach(key => {
        if (personalFields.includes(key)) {
          personalData[key] = formData[key];
        } else {
          industryDataObj[key] = formData[key];
        }
      });

      const resumeData = {
        ...personalData,
        user_id: user.id,
        industry: selectedIndustry || 'general',
        industry_data: industryDataObj || {},
        cover_letter_recipient: coverLetterData.recipient_name,
        cover_letter_company: coverLetterData.company_name,
        cover_letter_position: coverLetterData.position,
        cover_letter_body: coverLetterData.body,
        cover_letter_closing: coverLetterData.closing,
        updated_at: new Date()
      };

      let error;
      if (id && !isNewResume) {
        const { error: updateError } = await supabase
          .from('resumes')
          .update(resumeData)
          .eq('id', id);
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from('resumes')
          .insert([resumeData]);
        error = insertError;
      }

      if (error) {
        toast.error('Failed to save resume: ' + error.message);
      } else {
        toast.success('Resume saved successfully!');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save resume');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh', paddingTop: '80px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="py-4" style={{ paddingTop: '80px' }}>
      <Container fluid>
        {/* Header */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
          <Button variant="outline-secondary" onClick={() => navigate('/templates')}>
            <ArrowLeft size={18} className="me-2" /> Back
          </Button>
          
          {/* Tab switcher — active tab uses aqua gradient */}
          <div className="d-flex gap-2">
            <Button
              size="sm"
              onClick={() => setActiveTab('resume')}
              className={activeTab === 'resume' ? 'btn-hero-primary' : 'btn-outline-secondary'}
            >
              Resume
            </Button>
            <Button
              size="sm"
              onClick={() => setActiveTab('cover-letter')}
              className={activeTab === 'cover-letter' ? 'btn-hero-primary' : 'btn-outline-secondary'}
            >
              <FileText size={14} className="me-1" /> Cover Letter
            </Button>
          </div>

          {/* Action buttons — all use aqua gradient */}
          <div className="d-flex gap-2 flex-wrap">
            <Button
              onClick={saveResume}
              disabled={saving}
              className="btn-hero-primary d-flex align-items-center gap-2 px-4 py-2"
            >
              <Save size={18} />
              {saving ? 'Saving...' : 'Save Resume'}
            </Button>

            <ResumeDownload
              resumeId={id}
              formData={formData}
              industry={selectedIndustry}
              coverLetterData={coverLetterData}
              activeTab={activeTab}
              sections={sections}
            />

            <TXTDownload
              formData={formData}
              coverLetterData={coverLetterData}
            />
          </div>
        </div>

        {/* Industry Info */}
        {industryData && (
          <Alert variant="info" className="d-flex align-items-center gap-3 mb-4">
            <Sparkles size={20} />
            <div>
              <strong>{industryData.icon} {industryData.name}</strong>
              <span className="mx-2">•</span>
              <Badge bg="light" text="dark">{sections.length} sections</Badge>
            </div>
          </Alert>
        )}

        <Row className="g-4">
          {/* Left Column - Form */}
          <Col lg={7}>
            <Card className="shadow-sm border-0">
              <Card.Body className="p-4">
                {activeTab === 'resume' ? (
                  <ResumeForm
                    formData={formData}
                    setFormData={setFormData}
                    onSave={saveResume}
                    industrySections={industrySections}
                    currentIndustry={selectedIndustry}
                  />
                ) : (
                  // Cover Letter
                  <div>
                    <h5 className="fw-bold mb-3">Cover Letter</h5>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">Recipient Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={coverLetterData.recipient_name || ''}
                          onChange={(e) => setCoverLetterData(prev => ({ ...prev, recipient_name: e.target.value }))}
                          placeholder="Hiring Manager"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Company Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={coverLetterData.company_name || ''}
                          onChange={(e) => setCoverLetterData(prev => ({ ...prev, company_name: e.target.value }))}
                          placeholder="Company Name"
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label">Position / Job Title</label>
                        <input
                          type="text"
                          className="form-control"
                          value={coverLetterData.position || ''}
                          onChange={(e) => setCoverLetterData(prev => ({ ...prev, position: e.target.value }))}
                          placeholder="e.g. Software Engineer"
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label">Cover Letter Body</label>
                        <textarea
                          className="form-control"
                          rows={8}
                          value={coverLetterData.body || ''}
                          onChange={(e) => setCoverLetterData(prev => ({ ...prev, body: e.target.value }))}
                          placeholder="Write your cover letter here..."
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label">Closing</label>
                        <input
                          type="text"
                          className="form-control"
                          value={coverLetterData.closing || 'Sincerely'}
                          onChange={(e) => setCoverLetterData(prev => ({ ...prev, closing: e.target.value }))}
                          placeholder="Sincerely, Best regards, etc."
                        />
                      </div>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Right Column - Preview */}
          <Col lg={5}>
            <div className="sticky-top" style={{ top: '90px' }}>
              <Card className="shadow-sm border-0">
                <Card.Body className="p-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="fw-bold mb-0">
                      <Eye size={16} className="me-2" />
                      Live Preview
                    </h6>
                    <Badge bg="light" text="dark">
                      {activeTab === 'resume' ? 'Resume' : 'Cover Letter'}
                    </Badge>
                  </div>
                  <ResumePreview
                    data={formData}
                    industry={selectedIndustry}
                    activeTab={activeTab}
                    coverLetterData={coverLetterData}
                    formData={formData}
                    sections={sections}
                  />
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Builder;